import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatErr, downloadBlob } from "@/lib/api";
import { StatusChip } from "@/components/StatusChip";
import { SectionCard } from "@/components/Primitives";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import Attachments from "@/components/Attachments";
import { toast } from "sonner";
import { ArrowLeft, FileDown, Building2, Package, Coins, CalendarClock } from "lucide-react";

function Field({ label, value }) {
  return (
    <div className="py-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-sm text-slate-700 mt-0.5">{value ?? "—"}</p>
    </div>
  );
}

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [comm, setComm] = useState([]);

  const load = useCallback(async () => {
    try {
      const [ct, cm] = await Promise.all([api.get(`/contracts/${id}`), api.get("/commissions").catch(() => ({ data: { items: [] } }))]);
      setC(ct.data);
      setComm((cm.data.items || []).filter((x) => x.contract_id === id || x.contract_number === ct.data.contract_number));
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!c) return <div className="space-y-4"><div className="h-28 rounded-xl bg-white border animate-pulse" /><div className="h-64 rounded-xl bg-white border animate-pulse" /></div>;

  return (
    <div data-testid="page-contract-detail">
      <button onClick={() => navigate("/contracts")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" />Sales & Contracts</button>

      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#00C9E0] via-[#9ACC32] to-[#FE3967]" />
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 pt-1">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl font-bold text-[#1E2025]">{c.contract_number}</h1>
              <StatusChip value={c.agreement_status} />
              <StatusChip value={c.payment_status} />
            </div>
            <button onClick={() => c.business_id && navigate(`/businesses/${c.business_id}`)} className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0094A8]">
              <Building2 className="h-4 w-4" />{c.business_name}
            </button>
          </div>
          <div className="text-right">
            <p className="font-display text-2xl font-bold text-[#0094A8] tnum">{fmtMoney(c.contract_total)}</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => downloadBlob(`/pdf/contract/${c.id}`, `contract_${c.contract_number}.pdf`)} data-testid="contract-pdf"><FileDown className="h-4 w-4" />Download PDF</Button>
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2 mt-6">
        <SectionCard title="Contract Summary">
          <div className="grid grid-cols-2 divide-slate-100">
            <Field label="Term" value={c.term} />
            <Field label="Salesperson" value={c.salesperson_name} />
            <Field label="Start" value={c.start_date ? fmtDate(c.start_date) : "—"} />
            <Field label="End" value={c.end_date ? fmtDate(c.end_date) : "—"} />
            <Field label="Payment Status" value={c.payment_status} />
            <Field label="Agreement" value={c.agreement_status} />
          </div>
        </SectionCard>

        <SectionCard title="Products & Advertising">
          <div className="grid grid-cols-2">
            <Field label="Product" value={c.product_name} />
            <Field label="Territory" value={c.territory} />
            <Field label="Zone" value={c.zone} />
            <Field label="Add-ons" value={c.addons || (c.qr_addon ? "QR Code" : "—")} />
          </div>
        </SectionCard>

        <SectionCard title="Commercial Snapshot (frozen at creation)">
          <div className="grid grid-cols-2">
            <Field label="Monthly Rate" value={c.monthly_rate != null ? fmtMoney(c.monthly_rate) : "—"} />
            <Field label="Discount" value={c.discount != null ? (String(c.discount).includes("%") ? c.discount : fmtMoney(c.discount)) : "—"} />
            <Field label="Contract Total" value={fmtMoney(c.contract_total)} />
            <Field label="Created" value={c.created_at ? fmtDate(c.created_at) : "—"} />
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Historical pricing is locked — current rate changes never alter this contract.</p>
        </SectionCard>

        <SectionCard title="Commission" action={<Coins className="h-4 w-4 text-slate-300" />}>
          {comm.length === 0 ? <p className="text-sm text-slate-400 py-4">No commission snapshot linked to this contract.</p> : (
            <div className="divide-y divide-slate-100">
              {comm.map((m) => (
                <div key={m.id} className="flex items-center justify-between py-2.5 text-sm">
                  <div><p className="text-slate-700">{m.salesperson_name}</p><p className="text-xs text-slate-400">{m.commission_rule || (m.commission_percentage ? `${m.commission_percentage}%` : "Fixed")}</p></div>
                  <div className="flex items-center gap-2"><span className="font-bold text-[#0094A8] tnum">{fmtMoney(m.calculated_amount)}</span><StatusChip value={m.status} /></div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      <div className="mt-5"><Attachments relatedType="contracts" relatedId={id} /></div>
    </div>
  );
}
